import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { type BasePagination } from "../../../types/api.ts"
import { rejectWithMessage } from "../../../utils/handlers/reject-with-message.ts"
import * as services from "../api/services"
import type { SearchCustomersParams } from "../api/services.types.ts"
import type {
  Customer,
  CustomerMutateData,
  CustomerSearchResult
} from "../types/customer.types.ts"
import type {
  CustomerFilterOptions,
  CustomerFilters
} from "./customers.types.ts"

export const fetchCustomersFilterOptions =
  createAsyncThunk<CustomerFilterOptions>(
    "customers/fetchFilterOptions",
    async (_, { dispatch, rejectWithValue }) => {
      const response = await services.getCustomersFilterOptions()

      if (!response.ok) {
        dispatch(errorSave(response?.data?.details))
        return rejectWithValue(response.data)
      }

      return response.data || { status: [] }
    }
  )

export const fetchCustomers = createAsyncThunk<
  BasePagination<Customer>,
  CustomerFilters & { isMore?: boolean }
>(
  "customers/fetchCustomers",
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.getCustomers(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response?.data || { count: 0, next: null, results: [] }
  }
)

export const createCustomer = createAsyncThunk<Customer, CustomerMutateData>(
  "customers/createCustomer",
  async ({ is_invite, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.createCustomer(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    const createdCustomer = response?.data?.details?.data

    if (is_invite && createdCustomer?.id) {
      dispatch(inviteCustomer(createdCustomer.id))
    }

    return response!.data!.details.data
  }
)

export const updateCustomer = createAsyncThunk<
  Customer,
  CustomerMutateData & { id: string }
>("customers/updateCustomer", async (params, { dispatch, rejectWithValue }) => {
  const response = await services.updateCustomer(params)

  if (!response.ok) {
    return rejectWithMessage(response, dispatch, rejectWithValue)
  }

  const saved = response?.data?.details?.data

  // A 2xx carrying no record would return the card to view mode still showing
  // the old value — the defect this unwrap exists to fix — so refuse it.
  if (!saved) return rejectWithValue(response.data)

  return saved
})

export const deleteCustomer = createAsyncThunk<Customer, string>(
  "customers/deleteCustomer",
  async (customerId, { dispatch, rejectWithValue }) => {
    const response = await services.deleteCustomer(customerId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return { ...response.data, id: customerId } as unknown as Customer
  }
)

export const fetchCustomerDetails = createAsyncThunk<Customer, string>(
  "customers/fetchCustomerDetails",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getCustomerDetails(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data!
  }
)

export const resendInvite = createAsyncThunk<any, string>(
  "customers/resendInvite",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.resendInvite(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const suspendCustomer = createAsyncThunk<any, string>(
  "customers/suspendCustomer",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.suspendCustomer(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const reactivateCustomer = createAsyncThunk<any, string>(
  "customers/reactivateCustomer",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.reactivateCustomer(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const inviteCustomer = createAsyncThunk<any, string>(
  "customers/inviteCustomer",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.invite(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const searchCustomers = createAsyncThunk<
  CustomerSearchResult[] | BasePagination<CustomerSearchResult>,
  SearchCustomersParams
>(
  "customers/searchCustomers",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.searchCustomers(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data!
  }
)
